import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowDownward,
  ArrowUpward,
  InsertDriveFileOutlined,
  SubdirectoryArrowRight,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import {
  ENVIRONMENTS_OPTIONS,
  ROLES_OPTIONS,
  tableColumnWidths,
} from '../utils/constants';
import React from 'react';
import { type INavigationConfigDBItem } from '../types/types';
import { useTranslation } from 'react-i18next';

export interface AppConfigParentOption {
  id: string;
  label: string;
}

export const AppConfigRow = React.memo(
  ({
    configKey,
    config,
    onUpdate,
    depth = 0,
    parentOptions = [],
    positionLabel = '1 of 1',
    canMoveUp = false,
    canMoveDown = false,
    onMoveUp,
    onMoveDown,
  }: {
    configKey: string;
    config: INavigationConfigDBItem;
    onUpdate: (
      id: string,
      field: keyof INavigationConfigDBItem,
      value: unknown
    ) => void;
    depth?: number;
    parentOptions?: AppConfigParentOption[];
    positionLabel?: string;
    canMoveUp?: boolean;
    canMoveDown?: boolean;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
  }) => {
    const { t } = useTranslation();
    const indent = depth * 24;
    return (
      <TableRow
        sx={{
          bgcolor: depth > 0 ? 'action.hover' : undefined,
          '&:hover': { bgcolor: 'action.selected' },
        }}
      >
        <TableCell sx={{ width: '8%', verticalAlign: 'middle' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flexWrap: 'nowrap',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                minWidth: 36,
                fontWeight: 500,
                color: 'text.secondary',
              }}
              title={t('position_among_siblings')}
            >
              {positionLabel}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <IconButton
                size="small"
                onClick={onMoveUp}
                disabled={!canMoveUp}
                aria-label={t('move_up')}
                sx={{
                  p: 0.25,
                  color: canMoveUp ? 'primary.main' : 'action.disabled',
                }}
              >
                <ArrowUpward fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={onMoveDown}
                disabled={!canMoveDown}
                aria-label={t('move_down')}
                sx={{
                  p: 0.25,
                  color: canMoveDown ? 'primary.main' : 'action.disabled',
                }}
              >
                <ArrowDownward fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </TableCell>
        <TableCell sx={{ width: '14%', verticalAlign: 'middle' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              pl: `${indent}px`,
            }}
          >
            {depth > 0 ? (
              <SubdirectoryArrowRight fontSize="small" color="action" />
            ) : (
              <InsertDriveFileOutlined fontSize="small" />
            )}
            <Typography
              sx={{
                fontSize: '0.8rem',
                fontWeight: depth > 0 ? 400 : 500,
                color: 'text.primary',
              }}
              noWrap
            >
              {config.id || '—'}
            </Typography>
          </Box>
        </TableCell>
        <TableCell sx={{ width: '12%', verticalAlign: 'middle' }}>
          <TextField
            size="small"
            placeholder={t('path')}
            value={config.path ?? ''}
            onChange={(e) => {
              onUpdate(configKey, 'path', e.target.value);
            }}
            fullWidth
            inputProps={{ 'aria-label': t('path') }}
          />
        </TableCell>
        <TableCell sx={{ width: '10%', verticalAlign: 'middle' }}>
          <FormControl fullWidth size="small">
            <Select
              displayEmpty
              value={config.parentId ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                onUpdate(configKey, 'parentId', v === '' ? null : v);
              }}
              renderValue={(selected) => {
                if (!selected)
                  return (
                    <Typography variant="caption" color="text.secondary">
                      {t('top')}
                    </Typography>
                  );
                const opt = parentOptions.find((o) => o.id === selected);
                return (
                  <Typography variant="caption" noWrap>
                    {opt ? opt.label : selected}
                  </Typography>
                );
              }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 280 } } }}
            >
              <MenuItem value="">
                <em>{t('top_level')}</em>
              </MenuItem>
              {parentOptions.map((o) => (
                <MenuItem key={o.id} value={o.id} dense>
                  {o.label} ({o.id})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TableCell>
        <TableCell
          sx={{
            width: tableColumnWidths.displayLabel,
            verticalAlign: 'middle',
          }}
        >
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            value={config.label}
            onChange={(e) => {
              onUpdate(configKey, 'label', e.target.value);
            }}
            inputProps={{ style: { fontSize: '12px' } }}
          />
        </TableCell>
        <TableCell
          sx={{
            width: tableColumnWidths.allowedRoles,
            verticalAlign: 'middle',
          }}
        >
          <FormControl fullWidth size="small">
            <Select
              multiple
              displayEmpty
              value={config.roles}
              onChange={(e) => {
                const value = e.target.value;
                if (Array.isArray(value) && value.length > 0) {
                  onUpdate(configKey, 'roles', e.target.value);
                }
              }}
              input={<OutlinedInput />}
              renderValue={(selected: unknown) => {
                const arr = selected as string[];
                if (arr.length === 0)
                  return (
                    <Typography variant="caption" color="textSecondary">
                      {t('all_roles')}
                    </Typography>
                  );
                return (
                  <Typography variant="caption">
                    {arr.length} selected
                  </Typography>
                );
              }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
            >
              {ROLES_OPTIONS.map((r) => (
                <MenuItem key={r} value={r} dense>
                  <Checkbox checked={config.roles.includes(r)} size="small" />
                  <ListItemText primary={r} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TableCell>
        <TableCell
          sx={{
            width: tableColumnWidths.environments,
            verticalAlign: 'middle',
          }}
        >
          <FormControl fullWidth size="small">
            <Select
              multiple
              displayEmpty
              value={config.environments}
              onChange={(e) => {
                const value = e.target.value;
                if (Array.isArray(value) && value.length > 0) {
                  onUpdate(configKey, 'environments', e.target.value);
                }
              }}
              input={<OutlinedInput />}
              renderValue={(selected: unknown) => {
                const arr = selected as string[];
                if (arr.length === 0)
                  return (
                    <Typography variant="caption" color="error">
                      None
                    </Typography>
                  );
                return (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {arr.map((v: string) => (
                      <Chip
                        key={v}
                        label={v.substring(0, 4).toUpperCase()}
                        size="small"
                        sx={{ height: 20, fontSize: '0.6rem' }}
                        color="info"
                      />
                    ))}
                  </Box>
                );
              }}
            >
              {ENVIRONMENTS_OPTIONS.map((env) => (
                <MenuItem key={env} value={env} dense>
                  <Checkbox
                    checked={config.environments.includes(env)}
                    size="small"
                  />
                  <ListItemText primary={env} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TableCell>
        <TableCell
          sx={{
            textAlign: 'center',
            width: '8%',
            verticalAlign: 'middle',
          }}
        >
          <IconButton
            size="small"
            onClick={() => {
              onUpdate(configKey, 'invisible', !config.invisible);
            }}
            sx={{
              color: config.invisible ? '#EF4444' : '#10B981',
            }}
          >
            {config.invisible ? (
              <VisibilityOff fontSize="small" />
            ) : (
              <Visibility fontSize="small" />
            )}
          </IconButton>
        </TableCell>
        <TableCell
          sx={{
            textAlign: 'center',
            width: tableColumnWidths.showInSidebar,
            verticalAlign: 'middle',
          }}
        >
          <Switch
            size="small"
            checked={config.showPro ?? false}
            onChange={(_, checked) => {
              onUpdate(configKey, 'showPro', checked);
            }}
            color="primary"
            inputProps={{ 'aria-label': t('show_pro') }}
          />
        </TableCell>
      </TableRow>
    );
  },
  (prev, next) =>
    prev.configKey === next.configKey &&
    prev.config === next.config &&
    prev.depth === next.depth &&
    prev.parentOptions === next.parentOptions &&
    prev.positionLabel === next.positionLabel &&
    prev.canMoveUp === next.canMoveUp &&
    prev.canMoveDown === next.canMoveDown
);
AppConfigRow.displayName = 'AppConfigRow';
